import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import Select from 'app/frontend/base/components/Select'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterSpecSelect, {
  parseRoleOrSpec,
} from 'app/frontend/base/components/CharacterSpecSelect'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import CandidateForm from './CandidateForm'
import { useAddCandidate } from '../hooks'

const NEW_CHARACTER = 'new'

type FormProps = {
  candidates: wowaudit.GuildCandidate[]
  onCancel: () => void
  onAdded: () => void
}

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  candidates: wowaudit.GuildCandidate[]
}

const ExistingCharacterForm = ({
  character,
  onCancel,
  onAdded,
}: Omit<FormProps, 'candidates'> & {
  character: wowaudit.CharacterReference
}) => {
  const [roleOrSpec, setRoleOrSpec] = useState(
    character.currentSpecId?.toString(),
  )
  const { mutate, isPending } = useAddCandidate()

  return (
    <form
      className='flex flex-col gap-2'
      onSubmit={(event) => {
        event.preventDefault()
        if (!roleOrSpec) return

        mutate(
          { characterId: character.id, ...parseRoleOrSpec(roleOrSpec) },
          { onSuccess: onAdded },
        )
      }}
    >
      <CharacterSpecSelect
        characterClass={character.characterClass}
        value={roleOrSpec}
        setValue={setRoleOrSpec}
        includeRoles
        inModal
      />

      <div className='flex justify-end gap-2'>
        <Button type='button' variant='ghost' size='sm' onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type='submit'
          size='sm'
          disabled={!roleOrSpec}
          loading={isPending}
        >
          Add
        </Button>
      </div>
    </form>
  )
}

const AddOwnCandidateForm = ({ candidates, onCancel, onAdded }: FormProps) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const { guild, guildReference } = useGuild()
  const realmId = (guild?.realm ?? guildReference?.realm)?.id

  const eligibleCharacters = useMemo(() => {
    const candidateCharacterIds = new Set(
      candidates.map((candidate) => candidate.characterReference.id),
    )
    return (user?.characterReferences ?? []).filter(
      (character) =>
        character.realm.id === realmId &&
        !candidateCharacterIds.has(character.id),
    )
  }, [user, candidates, realmId])

  const [selected, setSelected] = useState(() => {
    const defaultCharacter =
      eligibleCharacters.find((character) => character.main) ??
      eligibleCharacters[0]
    return defaultCharacter ? defaultCharacter.id.toString() : NEW_CHARACTER
  })

  const selectedCharacter = eligibleCharacters.find(
    (character) => character.id.toString() === selected,
  )

  const options = [
    ...eligibleCharacters.map((character) => ({
      value: character.id.toString(),
      label: (
        <span
          className={cn(
            'flex items-center gap-2',
            classColours[character.characterClass].text,
          )}
        >
          <CharacterClassIcon
            size='small'
            characterClass={character.characterClass}
          />
          {character.name}
        </span>
      ),
      sortLabel: `0-${character.name}`,
    })),
    {
      value: NEW_CHARACTER,
      label: (
        <span className='flex items-center gap-2'>
          <Plus size={16} />
          {t('profile.manual-characters.add-new')}
        </span>
      ),
      sortLabel: '1',
    },
  ]

  return (
    <div className='flex flex-col gap-2'>
      {eligibleCharacters.length > 0 && (
        <Select
          options={options}
          value={selected}
          setValue={(value) => setSelected(value ?? NEW_CHARACTER)}
          isSearchable={false}
          inModal
        />
      )}

      {selectedCharacter ? (
        <ExistingCharacterForm
          key={selectedCharacter.id}
          character={selectedCharacter}
          onCancel={onCancel}
          onAdded={onAdded}
        />
      ) : (
        <CandidateForm inModal onCancel={onCancel} onAdded={onAdded} />
      )}
    </div>
  )
}

const AddOwnCandidateDialog = ({ open, onOpenChange, candidates }: Props) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className='max-w-md'>
      <DialogHeader>
        <DialogTitle>Add character</DialogTitle>
      </DialogHeader>
      {open && (
        <AddOwnCandidateForm
          candidates={candidates}
          onCancel={() => onOpenChange(false)}
          onAdded={() => {
            toast.success('Character added')
            onOpenChange(false)
          }}
        />
      )}
    </DialogContent>
  </Dialog>
)

export default AddOwnCandidateDialog
